/**
 * Per-form-instance runtime state.
 *
 * Replaces module-level `let` flags that previously lived at the top of
 * `useForm/index.tsx`. Each call to `useForm()` creates its own runtime
 * object (held in a `useRef`) so that multiple forms can coexist on the
 * same page without bleeding state into each other.
 *
 * The `leadId` value is kept as a module-level singleton because it is
 * intentionally shared across forms in the same session (e.g. main form
 * -> PII completion form -> second service form all reuse the same lead).
 */

export interface FormRuntime {
    isApiCallInProgress: boolean;
    startSendingData: boolean;
    submitStepClicked: boolean;
    clickedOnSubmit: boolean;
    queues: (() => Promise<void>)[];
    skippedValidationFields: string[];
}

export const createFormRuntime = (): FormRuntime => ({
    isApiCallInProgress: false,
    startSendingData: false,
    submitStepClicked: false,
    clickedOnSubmit: false,
    queues: [],
    skippedValidationFields: [],
});

// --- lead id singleton (intentionally shared across instances) ---
let _leadId: string | null = null;

// --- final-submit lock (shared across form instances by formId) ---
const _finalSubmitInFlightByFormId = new Map<number, boolean>();

export const leadIdSingleton = {
    get(): string | null {
        return _leadId;
    },
    set(value: string | null): void {
        _leadId = value;
    },
};

export const finalSubmitLock = {
    tryAcquire(formId: number): boolean {
        if (_finalSubmitInFlightByFormId.get(formId)) return false;
        _finalSubmitInFlightByFormId.set(formId, true);
        return true;
    },
    release(formId: number): void {
        _finalSubmitInFlightByFormId.delete(formId);
    },
    isLocked(formId: number): boolean {
        return !!_finalSubmitInFlightByFormId.get(formId);
    },
};
